import { useState, useEffect } from "react";
import { VictoryPie, VictoryChart, VictoryTooltip } from "victory";
import { addDays, format, startOfWeek, endOfWeek } from "date-fns";

export default function TaskGraph() {
  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [tasks, setTasks] = useState([]);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [graphData, setGraphData] = useState([]);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState("All");

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const response = await fetch("https://api.insightos.co.uk/api/taskgraph", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ fromDate, toDate }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setTasks(result.data);
        const tasks = Array.isArray(result.data) ? result.data : [];

        const fetchedReports = Array.isArray(result.reports)
          ? result.reports
          : result.data && Array.isArray(result.data.reports)
          ? result.data.reports
          : [];
        setReports(fetchedReports);

        const filteredTasks = tasks.filter((task) => task.Task !== "Break");

        const graphDataMap = filteredTasks.reduce((acc, task) => {
          const name = task.Name || "Unknown";
          if (!acc[name]) {
            acc[name] = { Name: name, Completed: 0, Incomplete: 0, IncompleteTasks: [] };
          }
          if (task.Completed === 1) {
            acc[name].Completed += 1;
          } else {
            acc[name].Incomplete += 1;
            acc[name].IncompleteTasks.push(task.Task || "Unnamed Task");
          }
          return acc;
        }, {});

        const validatedGraphData = Object.values(graphDataMap).map((entry) => ({
          ...entry,
          Completed: Number(entry.Completed) || 0,
          Incomplete: Number(entry.Incomplete) || 0,
        }));

        setGraphData(validatedGraphData);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
      setLoading(false);
    };

    getData();
  }, [fromDate, toDate]);

  // Format report content: split by ## and **, remove those characters, and render as lines
  const formatReportContent = (content) => {
    if (!content) return [];
    // Replace ###, ***, ##, *, --- with newlines, then split, trim, and filter
    let lines = content
      .replace(/###/g, "\n")
      .replace(/\*\*\*/g, "\n")
      .replace(/\s*---\s*/g, "\n")
      .replace(/\s*\*\s*/g, "\n")
      .replace(/\s*##\s*/g, "\n")
      .split("\n")
      .map((line) =>
        line
          .replace(/^(\*+|#+)\s*/, "") // Remove leading **, ##, etc.
          .replace(/(\*+|#+)$/, "") // Remove trailing **, ##, etc.
          .trim()
      )
      .filter((line) => line.length > 0);
    return lines;
  };

  const handleReportClick = (reportContent) => {
    setSelectedReport(formatReportContent(reportContent));
  };

  // Prepare pie chart data based on selected staff
  const getPieChartData = () => {
    if (selectedStaff === "All") {
      const totalCompleted = graphData.reduce((sum, item) => sum + item.Completed, 0);
      const totalIncomplete = graphData.reduce((sum, item) => sum + item.Incomplete, 0);
      
      return [
        { x: "Completed", y: totalCompleted, fill: "#22c55e" },
        { x: "Incomplete", y: totalIncomplete, fill: "#ef4444" }
      ];
    } else {
      const staffData = graphData.find(item => item.Name === selectedStaff);
      if (!staffData) return [];
      
      return [
        { x: "Completed", y: staffData.Completed, fill: "#22c55e" },
        { x: "Incomplete", y: staffData.Incomplete, fill: "#ef4444" }
      ];
    }
  };

  // Get staff list for dropdown
  const getStaffList = () => {
    return ["All", ...graphData.map(item => item.Name)];
  };

  const taskTally = (tasks) => {
    const roleMap = tasks.reduce((acc, task) => {
      const role = task.Role || "Unknown";
      const taskName = task.Task || "Unnamed Task";
      if (taskName === "Break") {
        return acc;
      }
      if (!acc[role]) {
        acc[role] = { Role: role, tasks: {} };
      }
      if (!acc[role].tasks[taskName]) {
        acc[role].tasks[taskName] = { Task: taskName, Completed: 0, Incomplete: 0 };
      }
      if (task.Completed === 1) {
        acc[role].tasks[taskName].Completed += 1;
      } else {
        acc[role].tasks[taskName].Incomplete += 1;
      }
      return acc;
    }, {});
    return Object.values(roleMap).map((roleEntry) => ({
      Role: roleEntry.Role,
      tasks: Object.values(roleEntry.tasks),
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-100 px-3 py-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-4 rounded-3xl border border-white/35 bg-gradient-to-r from-indigo-950 via-sky-700 to-emerald-500 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.28)] sm:mb-6 sm:p-6">
          <div className="text-center">
            <h1 className="mb-2 flex items-center justify-center text-xl font-bold text-white sm:text-2xl lg:text-3xl">
              <span className="text-2xl sm:text-3xl mr-2">📊</span>
              Task Analytics
            </h1>
            <p className="text-sm text-sky-100/90 sm:text-base">
              Track and analyze task completion across your team
            </p>
          </div>
        </div>

        {/* Controls Section */}
        <div className="mb-4 rounded-3xl border border-white/35 bg-white/75 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl sm:mb-6 sm:p-6">
          <div className="space-y-4">
            {/* Date Inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="fromDate" className="block text-sm font-semibold text-gray-700 mb-2">
                  📅 From Date
                </label>
                <input
                  type="date"
                  id="fromDate"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full rounded-2xl border border-white/45 bg-white/85 px-4 py-3 font-medium text-gray-900 shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all duration-200 hover:border-sky-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 touch-manipulation"
                />
              </div>
              <div>
                <label htmlFor="toDate" className="block text-sm font-semibold text-gray-700 mb-2">
                  📅 To Date
                </label>
                <input
                  type="date"
                  id="toDate"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full rounded-2xl border border-white/45 bg-white/85 px-4 py-3 font-medium text-gray-900 shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all duration-200 hover:border-sky-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 touch-manipulation"
                />
              </div>
            </div>
            
            {/* Staff Selector */}
            <div>
              <label htmlFor="staffSelect" className="block text-sm font-semibold text-gray-700 mb-2">
                👤 Select Staff Member
              </label>
              <select
                id="staffSelect"
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                className="w-full rounded-2xl border border-white/45 bg-white/85 px-4 py-3 text-base font-medium text-gray-900 shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all duration-200 hover:border-sky-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 touch-manipulation"
              >
                {getStaffList().map(staff => (
                  <option key={staff} value={staff}>
                    {staff === "All" ? "🌟 All Staff Members" : `👤 ${staff}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        {graphData.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="rounded-2xl border border-white/35 bg-white/75 p-4 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-5">
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-green-600 mb-1">
                  {selectedStaff === "All" 
                    ? graphData.reduce((sum, item) => sum + item.Completed, 0)
                    : graphData.find(item => item.Name === selectedStaff)?.Completed || 0
                  }
                </div>
                <div className="text-xs sm:text-sm font-semibold text-green-700">
                  ✅ Completed
                </div>
              </div>
            </div>
            
            <div className="rounded-2xl border border-white/35 bg-white/75 p-4 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-5">
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-red-600 mb-1">
                  {selectedStaff === "All" 
                    ? graphData.reduce((sum, item) => sum + item.Incomplete, 0)
                    : graphData.find(item => item.Name === selectedStaff)?.Incomplete || 0
                  }
                </div>
                <div className="text-xs sm:text-sm font-semibold text-red-700">
                  ⏳ Incomplete
                </div>
              </div>
            </div>
            
            <div className="rounded-2xl border border-white/35 bg-white/75 p-4 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-5">
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-blue-600 mb-1">
                  {selectedStaff === "All" 
                    ? graphData.length
                    : (graphData.find(item => item.Name === selectedStaff)?.Completed || 0) + 
                      (graphData.find(item => item.Name === selectedStaff)?.Incomplete || 0)
                  }
                </div>
                <div className="text-xs sm:text-sm font-semibold text-blue-700">
                  {selectedStaff === "All" ? "👥 Staff" : "📋 Total"}
                </div>
              </div>
            </div>
            
            <div className="rounded-2xl border border-white/35 bg-white/75 p-4 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-5">
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-purple-600 mb-1">
                  {(() => {
                    if (selectedStaff === "All") {
                      const totalCompleted = graphData.reduce((sum, item) => sum + item.Completed, 0);
                      const totalTasks = graphData.reduce((sum, item) => sum + item.Completed + item.Incomplete, 0);
                      return totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;
                    } else {
                      const staffData = graphData.find(item => item.Name === selectedStaff);
                      if (!staffData) return 0;
                      const total = staffData.Completed + staffData.Incomplete;
                      return total > 0 ? Math.round((staffData.Completed / total) * 100) : 0;
                    }
                  })()}%
                </div>
                <div className="text-xs sm:text-sm font-semibold text-purple-700">
                  📈 Success Rate
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pie Chart Section */}
        <div className="mb-4 rounded-3xl border border-white/35 bg-white/75 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl sm:mb-6 sm:p-6">
          <div className="text-center mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              📊 {selectedStaff === "All" ? "Overall Completion" : `${selectedStaff}'s Progress`}
            </h3>
            <p className="text-sm text-gray-600">
              {selectedStaff === "All" ? "Team-wide task completion overview" : "Individual performance breakdown"}
            </p>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12 sm:py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Loading analytics...</p>
              </div>
            </div>
          ) : graphData.length === 0 ? (
            <div className="flex items-center justify-center py-12 sm:py-16">
              <div className="text-center">
                <span className="text-5xl sm:text-6xl mb-4 block">📊</span>
                <p className="text-gray-500 text-lg font-medium mb-2">No Data Available</p>
                <p className="text-gray-400 text-sm">Try adjusting your date range</p>
              </div>
            </div>
          ) : getPieChartData().reduce((sum, item) => sum + item.y, 0) === 0 ? (
            <div className="flex items-center justify-center py-12 sm:py-16">
              <div className="text-center">
                <span className="text-4xl sm:text-5xl mb-4 block">😴</span>
                <p className="text-gray-500 text-lg font-medium mb-2">No Tasks Found</p>
                <p className="text-gray-400 text-sm">
                  {selectedStaff === "All" ? "No tasks recorded for this period" : `No tasks found for ${selectedStaff}`}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-12">
              {/* Pie Chart */}
              <div className="flex-shrink-0 order-1 lg:order-1">
                <VictoryPie
                  data={getPieChartData()}
                  width={320}
                  height={320}
                  innerRadius={50}
                  padAngle={3}
                  labelRadius={130}
                  colorScale={["#22c55e", "#ef4444"]}
                  style={{
                    labels: { 
                      fontSize: 14, 
                      fontWeight: "bold",
                      fill: "#374151"
                    }
                  }}
                  labelComponent={
                    <VictoryTooltip 
                      style={{ 
                        fill: "#374151",
                        fontSize: 12,
                        fontWeight: "bold"
                      }}
                      flyoutStyle={{
                        stroke: "#374151",
                        strokeWidth: 2,
                        fill: "#f9fafb",
                      }}
                    />
                  }
                  animate={{
                    duration: 1000,
                    onLoad: { duration: 500 }
                  }}
                />
              </div>
              
              {/* Legend and Progress */}
              <div className="w-full lg:w-80 order-2 lg:order-2">
                <div className="space-y-4">
                  <div className="rounded-2xl border border-white/35 bg-white/85 p-4 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-green-500 rounded-full mr-3"></div>
                        <span className="text-sm font-semibold text-green-800">Completed Tasks</span>
                      </div>
                      <span className="text-xl font-bold text-green-800">
                        {getPieChartData().find(item => item.x === "Completed")?.y || 0}
                      </span>
                    </div>
                  </div>
                  
                  <div className="rounded-2xl border border-white/35 bg-white/85 p-4 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
                        <span className="text-sm font-semibold text-red-800">Incomplete Tasks</span>
                      </div>
                      <span className="text-xl font-bold text-red-800">
                        {getPieChartData().find(item => item.x === "Incomplete")?.y || 0}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="rounded-2xl border border-white/35 bg-white/85 p-4 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-gray-700">Overall Progress</span>
                      <span className="text-lg font-bold text-gray-900">
                        {(() => {
                          const completed = getPieChartData().find(item => item.x === "Completed")?.y || 0;
                          const incomplete = getPieChartData().find(item => item.x === "Incomplete")?.y || 0;
                          const total = completed + incomplete;
                          return total > 0 ? Math.round((completed / total) * 100) : 0;
                        })()}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-green-600 h-4 rounded-full transition-all duration-1000 ease-out"
                        style={{
                          width: `${(() => {
                            const completed = getPieChartData().find(item => item.x === "Completed")?.y || 0;
                            const incomplete = getPieChartData().find(item => item.x === "Incomplete")?.y || 0;
                            const total = completed + incomplete;
                            return total > 0 ? (completed / total) * 100 : 0;
                          })()}%`
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Task Tally by Role Section */}
        <div className="mb-4 rounded-3xl border border-white/35 bg-white/75 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl sm:mb-6 sm:p-6">
          <div className="mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 flex items-center">
              <span className="text-xl mr-2">📋</span>
              Task Breakdown by Role
            </h2>
            <p className="text-sm text-gray-600">
              Detailed analysis of task completion across different roles
            </p>
          </div>
          
          {taskTally(tasks).length > 0 ? (
            <div className="space-y-4 sm:space-y-6">
              {taskTally(tasks).map((role) => (
                <div key={role.Role} className="rounded-2xl border border-white/35 bg-white/85 p-4 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-6">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
                        <span className="text-lg mr-2">👤</span>
                        {role.Role}
                      </h3>
                      <div className="text-xs sm:text-sm text-gray-600 bg-white px-3 py-1 rounded-full border">
                        {role.tasks.length} task{role.tasks.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {role.tasks.map((task) => (
                      <div key={task.Task} className="rounded-xl border border-white/35 bg-white/90 p-4 shadow-[0_10px_22px_rgba(15,23,42,0.08)] backdrop-blur-xl">
                        <div className="mb-3">
                          <h4 className="font-semibold text-sm text-gray-900 leading-tight line-clamp-2">
                            {task.Task}
                          </h4>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                              <span className="text-xs font-medium text-green-700">Completed</span>
                            </div>
                            <span className="text-sm font-bold text-green-800">{task.Completed}</span>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                              <span className="text-xs font-medium text-red-700">Incomplete</span>
                            </div>
                            <span className="text-sm font-bold text-red-800">{task.Incomplete}</span>
                          </div>
                          
                          {(task.Completed + task.Incomplete) > 0 && (
                            <>
                              <div className="pt-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-gray-600">Success Rate</span>
                                  <span className="text-xs font-bold text-gray-800">
                                    {Math.round((task.Completed / (task.Completed + task.Incomplete)) * 100)}%
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-500"
                                    style={{
                                      width: `${Math.round((task.Completed / (task.Completed + task.Incomplete)) * 100)}%`
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 sm:py-12">
              <span className="text-4xl sm:text-5xl mb-4 block">📋</span>
              <p className="text-gray-500 text-lg font-medium mb-2">No Role Data Available</p>
              <p className="text-gray-400 text-sm">Tasks will appear here once data is loaded</p>
            </div>
          )}
        </div>

        {/* Reports Section */}
        {reports.length > 0 && (
          <div className="mb-4 rounded-3xl border border-white/35 bg-white/75 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl sm:mb-6 sm:p-6">
            <div className="mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 flex items-center">
                <span className="text-xl mr-2">📄</span>
                Generated Reports
              </h2>
              <p className="text-sm text-gray-600">
                Access detailed reports for the selected date range
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {reports.map((report) => (
                <button
                  key={report.ReportDate}
                  className="rounded-xl border border-white/35 bg-white/85 p-4 shadow-[0_10px_26px_rgba(15,23,42,0.1)] transition-all duration-200 active:scale-95 touch-manipulation group hover:bg-white"
                  onClick={() => handleReportClick(report.Content)}
                >
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl mb-2 group-hover:scale-110 transition-transform duration-200">
                      📊
                    </div>
                    <div className="text-sm sm:text-base font-bold text-blue-900 mb-1">
                      {format(new Date(report.ReportDate), "dd/MM/yyyy")}
                    </div>
                    <div className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded-full border border-blue-200">
                      {report.DailyReport === 1 ? "Daily Report" : "Company Report"}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Selected Report Content */}
        {selectedReport && (
          <div className="rounded-3xl border border-white/35 bg-white/75 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl sm:p-6">
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <span className="text-xl mr-2">📋</span>
                  Report Details
                </h3>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 touch-manipulation"
                  aria-label="Close report"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="max-h-96 overflow-y-auto rounded-2xl border border-white/35 bg-white/85 p-4 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:max-h-[500px] sm:p-6">
              <div className="text-gray-800 space-y-3">
                {selectedReport.map((line, idx) => (
                  <div key={idx} className="text-sm sm:text-base leading-relaxed bg-white border-l-4 border-blue-400 pl-4 py-2 rounded-r-lg shadow-sm">
                    {line}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
